'use client';
import { useState, type SyntheticEvent } from 'react';
import { useToast } from '@practium/ui';
import { useResetPassword } from '@practium/features/api';
import { AuthFormWrapper } from '../AuthFormWrapper/AuthFormWrapper';
import { AuthInput } from '../AuthInput/AuthInput';
import { AuthButton } from '../AuthButton/AuthButton';
import { validatePassword, validatePasswordMatch } from '../../validations/auth.validation';
import styles from './ResetPasswordForm.module.css';

interface ResetPasswordFormProps {
	token: string;
	onSuccess?: () => void;
	onRequestNewLink?: () => void;
}

export const ResetPasswordForm = ({ token, onSuccess, onRequestNewLink }: ResetPasswordFormProps) => {
	const toast = useToast();
	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [tokenInvalid, setTokenInvalid] = useState(!token);
	const { mutate: resetPassword, isPending } = useResetPassword();

	const handleSubmit = (e: SyntheticEvent) => {
		e.preventDefault();

		const passwordValidation = validatePassword(password);
		if (!passwordValidation.isValid) {
			toast.error(passwordValidation.errors[0]);
			return;
		}

		const passwordMatchValidation = validatePasswordMatch(password, confirmPassword);
		if (!passwordMatchValidation.isValid) {
			toast.error(passwordMatchValidation.error!);
			return;
		}

		resetPassword(
			{ token, password },
			{
				onSuccess: () => {
					toast.success('Senha redefinida com sucesso! Faça login com sua nova senha.');
					onSuccess?.();
				},
				onError: (err) => {
					const message = err instanceof Error ? err.message : 'Erro ao redefinir senha';
					toast.error(message);
					if (message.toLowerCase().includes('token')) {
						setTokenInvalid(true);
					}
				},
			}
		);
	};

	if (tokenInvalid) {
		return (
			<div className={styles.invalidToken}>
				<p className={styles.invalidTokenText}>
					Esse link de recuperação é inválido, expirou ou já foi utilizado.
				</p>
				<AuthButton type="button" variant="primary" fullWidth onClick={onRequestNewLink}>
					Solicitar novo link
				</AuthButton>
			</div>
		);
	}

	return (
		<AuthFormWrapper onSubmit={handleSubmit} showDivider={false}>
			<AuthInput
				label="Nova senha"
				isPassword
				value={password}
				onChange={(e) => setPassword(e.target.value)}
				placeholder="abc123"
				required
			/>
			<AuthInput
				label="Confirmar nova senha"
				isPassword
				value={confirmPassword}
				onChange={(e) => setConfirmPassword(e.target.value)}
				placeholder="abc123"
				required
			/>

			<div className={styles.buttons}>
				<AuthButton type="submit" variant="primary" fullWidth isLoading={isPending}>
					Redefinir senha
				</AuthButton>
			</div>
		</AuthFormWrapper>
	);
};
